import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { CsvExportModule } from '@ag-grid-community/csv-export';

import { UDCTable } from '../../../../../UiComponentsLibrary/UDC';
import CustomAlert from '../../../../../UiComponentsLibrary/CustomAlert';
import CustomSpinner from '../../../../../UiComponentsLibrary/CustomSpinner';
import { CustomConfirmationModal } from '../../../../../UiComponentsLibrary/CustomConfirmationModal';
import { CustomSnackbar } from '../../../../../UiComponentsLibrary/CustomSnackbar';
import { SnackbarVariants } from '../../../../../UiComponentsLibrary/CustomSnackbar/constants/snackbarVariants';

import { UDCTableHeaderPanel, UDCTableFooterPanel } from '../../../../../Shared/components/UDC';
import { ErrorDisplay } from '../../../../../Shared/components/ErrorDisplay/ErrorDisplay';
import { useAppContext } from '../../../../../Shared/store/appContext';
import { UserRoles } from '../../../../../Shared/constants/userRoles';

import { ApiError } from '../../../../../Shared/interfaces/ApiError';
import { ActionIconConfig } from '../../config/actionButtonConfig';
import { QueueSheetManagementRows } from '../../interface/QueueSheetInterface';
import { useBulkGtmStatusMutation } from '../../hooks/useBulkGtmStatusMutation';
import BulkEditBar, { BulkEditSelection } from '../BulkEditBar/BulkEditBar';
import {
  LoadingSpinnerFlexContainer,
  QueueSheetFlex,
} from './BookingTable.styles';
import { useBookingTable } from './hooks/useBookingTable';
import { extractBackendFilters } from './utils/exportFilterUtils';
import { DownloadBookingFilters } from '../../utils/downloadFilters';

ModuleRegistry.registerModules([ClientSideRowModelModule, CsvExportModule]);

interface BookingTableProps {
  bookingData: QueueSheetManagementRows[];
  isLoading: boolean;
  actionButtonConfig: ActionIconConfig[];
  bookingError?: ApiError;
  brands: string[];
  slotPackages: string[];
  artEventId?: string;
  handleExportBooking?: (filters?: DownloadBookingFilters) => void;
  isDownloadDisabled?: boolean;
  isCancelledView?: boolean;
}

const BookingTable = ({
  bookingData,
  isLoading,
  actionButtonConfig,
  bookingError,
  brands,
  slotPackages,
  artEventId = '',
  handleExportBooking = () => {},
  isDownloadDisabled = false,
  isCancelledView = false,
}: BookingTableProps) => {
  const { t } = useTranslation();

  // Bulk GTM status change is a super-admin-only action, and GTM status can only change on ACTIVE
  // bookings, so selection is enabled only for super admins on the active (non-cancelled) view.
  const { state: { currentUser } } = useAppContext();
  const enableSelection = currentUser?.role === UserRoles.SUPER_ADMIN && !isCancelledView;

  const { gridProps, exportProps, filterProps, selectionProps } = useBookingTable({
    bookingData,
    actionButtonConfig,
    brands,
    slotPackages,
    artEventId,
    enableSelection,
  });

  const [pendingEdit, setPendingEdit] = useState<BulkEditSelection | null>(null);
  const [bulkSuccessMessage, setBulkSuccessMessage] = useState<string | null>(null);

  const bulkMutation = useBulkGtmStatusMutation(artEventId, () => {
    setPendingEdit(null);
    selectionProps.clearSelection();
    setBulkSuccessMessage(t('bulk-gtm-success-message'));
  });

  const handleBulkApply = useCallback((selection: BulkEditSelection) => {
    setBulkSuccessMessage(null);
    setPendingEdit(selection);
  }, []);

  const handleBulkConfirm = useCallback(() => {
    if (!pendingEdit) return;
    const bookingIds = selectionProps.selectedRows.map((row) => row.bookingId);
    // Dispatch on the chosen column. Only GTM status is backend-supported today; add a branch
    // here (and a BULK_EDITABLE_COLUMNS entry) when another column becomes bulk-editable.
    if (pendingEdit.columnKey === 'gtmStatus') {
      bulkMutation.mutate({ bookingIds, gtmStatus: pendingEdit.value });
    }
  }, [pendingEdit, bulkMutation, selectionProps.selectedRows]);

  // Send the brand/slot/GTM filters currently applied in the grid to the backend
  // export so the downloaded sheet reflects the on-screen filters.
  const onExport = useCallback(() => {
    const filters = extractBackendFilters(gridProps.gridApi?.getFilterModel());
    handleExportBooking(filters);
  }, [gridProps.gridApi, handleExportBooking]);

  const headerPanelElement = useMemo(() => (
    <UDCTableHeaderPanel
      filterGroups={filterProps.filterGroups}
      columnExtendedDefs={filterProps.columnVisibilityConfig}
      columnManagerPresetDefs={filterProps.columnManagerPresets}
      onExport={onExport}
      bookingData={filterProps.filteredBookingData}
      gridApi={gridProps.gridApi}
      onFieldFilterChange={filterProps.setFieldFilter}
      isExportDisabled={isDownloadDisabled}
    />
  ), [filterProps.filterGroups, filterProps.columnVisibilityConfig, filterProps.columnManagerPresets, onExport, filterProps.filteredBookingData, gridProps.gridApi, filterProps.setFieldFilter, isDownloadDisabled]);

  const footerPanelElement = useMemo(
    () => (
      <UDCTableFooterPanel
        pageSizes={[10, 20, 50, 100]}
        hideOnSinglePage={false}
      />
    ),
    []
  );

  if (isLoading)
    return (
      <LoadingSpinnerFlexContainer
        id="loading-spinner-container"
        justifyContent="center"
        alignItems="center"
      >
        <CustomSpinner />
      </LoadingSpinnerFlexContainer>
    );

  if (bookingError) {
    return (
      <CustomAlert
        alertMessage={t('no-booking-data-message')}
        variant="error"
      />
    );
  }

  if (!bookingData.length) {
    const isFiltered = gridProps.gridApi?.isAnyFilterPresent() || false;
    const message = isFiltered ? t('no-search-results-found') : t('no-bookings-found');
    return <CustomAlert alertMessage={message} variant="info" />;
  }

  return (
    <QueueSheetFlex id="booking-table-container" direction="column">
      {exportProps.exportError && (
        <CustomAlert
          alertMessage={exportProps.exportError}
          variant="error"
          onClose={() => exportProps.setExportError(null)}
        />
      )}
      <CustomSnackbar
        id="bulk-gtm-success-snackbar"
        variant={SnackbarVariants.SUCCESS}
        message={bulkSuccessMessage ?? ''}
        isVisible={!!bulkSuccessMessage}
        onClose={() => setBulkSuccessMessage(null)}
      />
      {enableSelection && selectionProps.selectedRows.length > 0 && (
        <BulkEditBar
          selectedCount={selectionProps.selectedRows.length}
          onApply={handleBulkApply}
          onClear={selectionProps.clearSelection}
          isPending={bulkMutation.isPending}
        />
      )}
      <UDCTable
        uidPrefix="booking-table"
        locale="en-US"
        timezone="Asia/Kolkata"
        columnDefs={gridProps.columnDefs}
        rowData={filterProps.filteredBookingData}
        gridOptions={gridProps.gridOptions}
        onGridReady={gridProps.onGridReady}
        onFilterChanged={gridProps.onFilterChanged}
        headerPanel={headerPanelElement}
        footerPanel={footerPanelElement}
      />
      <CustomConfirmationModal
        isOpen={pendingEdit !== null}
        title={t('bulk-gtm-modal-title')}
        message={t('bulk-gtm-modal-message', { count: selectionProps.selectedRows.length })}
        onConfirmCb={handleBulkConfirm}
        onCancelCb={() => setPendingEdit(null)}
        confirmButtonText={t('bulk-gtm-modal-confirm-button')}
        confirmButtonDisabled={bulkMutation.isPending}
      >
        {bulkMutation.isError && <ErrorDisplay error={bulkMutation.error} />}
      </CustomConfirmationModal>
    </QueueSheetFlex>
  );
};

export default BookingTable;
